import React, { FC, useCallback, useId, FormEvent, useEffect, useRef, useState } from 'react';
import chevron_down from 'assets/icons/chevron_down.svg';
import axiosInstance from 'config/axiosInstance';
import { DialogProps, NewDataField } from './dialogInterface';
import { useTranslation } from 'react-i18next';
import { useModalDialog } from 'reusable/useModalDialog';
import { businessDateTimeToIsoKeeping, businessLocalTimeStatus } from 'config/time';
import { REASON_FIELD_TYPE, REASON_MAX_LENGTH } from './dialogInterface';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';

const EditDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  confirmLabel,
  cancelLabel,
  fields,
  handleSubmit,
  deviceId,
  attendanceId,
  vehicleId,
  taskSessionId, // Add TaskSessionId
}) => {
  const { t } = useTranslation('attendance');
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);
  // ADM-16。項目名（label）と入力欄を結び付ける（読み上げで何の欄か分かるように）。
  const fieldId = useId();
  // ADM-15。保存が終わるまで確定ボタンを押せなくする（続けて押すと同じ内容を2回送っていた）。
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newData, setNewData] = useState<NewDataField>(
    fields.reduce((acc, field) => {
      acc[field.name] = field.value || null;
      return acc;
    }, {} as NewDataField)
  );

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});

  // EV-276。開いてから利用者が変えた欄。取り直しの応答はこの欄を上書きしない
  // （以前は応答で入力を丸ごと置き換え、応答の前に入力した値が元に戻っていた）。
  const touched = useRef<Set<string>>(new Set());
  /** 取り直しの応答の値を入れる。利用者が変えた欄は今の値のまま。 */
  const applyFetched = useCallback(
    (populatedData: NewDataField) =>
      setNewData((current) => {
        const merged = { ...populatedData };
        touched.current.forEach((name) => {
          merged[name] = current[name];
        });
        return merged;
      }),
    []
  );

  useEffect(() => {
    // 閉じたら覚えた欄を空にする（次に開いたときは応答の値で埋める）。
    if (!isOpen) touched.current.clear();
  }, [isOpen]);

  // EV-322（ADM-15）。開いたときに最新の値を取り直す。読めなかったこと（一覧の値・前に開いた記録の値と
  // 区別する）。最後の要求の応答だけを反映する（閉じた後・別の記録を開いた後に届いた応答は捨てる）。
  const [fetchError, setFetchError] = useState<unknown>(null);
  // EV-334（2026-10-14 PM 判断）。最新の値を読み終えた記録（取り直しの URL）。読み終えるまで保存を止める。
  // 以前は取り直しに失敗した後だけ止め、最初の読み込みの間は一覧の値のまま保存できた。
  // URL で持つので、別の記録へ切り替えた直後の描画でも前の記録の「読み終えた」を使わない。
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const beginRequest = useLatestRequest();
  const latest = deviceId
    ? { url: `/v1/devices/${deviceId}/`, label: 'device' }
    : attendanceId
    ? { url: `/v1/attendances/${attendanceId}/`, label: 'attendance' }
    : vehicleId
    ? { url: `/v1/vehicles/${vehicleId}/`, label: 'vehicle' }
    : taskSessionId
    ? { url: `/v1/task_sessions/${taskSessionId}/`, label: 'task session' }
    : null;
  const latestUrl = latest?.url ?? null;
  const latestLabel = latest?.label ?? '';
  // PR #258 の Codex の P2 4266034596（FR-AC9）。欄（fields）は ref で読む。候補の情報（options・fetchError）が
  // 変わって fields が作り直されただけで、詳細を取り直したり、欄を渡された値に戻したりしない。
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const fetchLatest = useCallback(async () => {
    if (!latestUrl) return;
    const isCurrent = beginRequest();
    setFetchError(null);
    // PR #268 の照合の P3-1。取り直しを始めたら「読み終えた」を消す。開いたまま 3→4→3 と戻したとき、
    // 前に 3 を読み終えた印が残り、戻した 3 の取り直しの間に一覧の値のまま保存できた。
    // 失敗のときもこの印は消えたまま（以前は失敗の分岐でも消していたが、ここで消すので重なる）。
    setLoadedUrl(null);
    try {
      const response = await axiosInstance.get(latestUrl);
      if (!isCurrent()) return;
      const fetched = response.data;
      const populatedData = fieldsRef.current.reduce((acc, field) => {
        if (latestLabel !== 'device') {
          acc[field.name] = fetched[field.name] || field.value || null;
        } else if (field.name !== 'status') {
          acc[field.name] = fetched[field.name] || null;
        } else {
          acc[field.name] =
            fetched.status === 'using' ? `${fetched.status}:${fetched.user}` : fetched.status;
        }
        return acc;
      }, {} as NewDataField);
      applyFetched(populatedData);
      setLoadedUrl(latestUrl);
    } catch (error) {
      if (!isCurrent()) return;
      console.error(`Failed to fetch ${latestLabel} data:`, error);
      setFetchError(error);
    }
  }, [latestUrl, latestLabel, applyFetched, beginRequest]);

  useEffect(() => {
    if (!isOpen) {
      // 閉じたら、取り直しの途中の応答を捨て、失敗と読み終えた印を消す。
      beginRequest();
      setFetchError(null);
      setLoadedUrl(null);
      return;
    }
    if (!latestUrl) return;
    // EV-322（FR-AC5）。開いたときは、渡された値（一覧の値）から始める。以前は前に開いた記録の値が残り、
    // 取り直しに失敗するとその値のまま保存できた。利用者が変えた欄は今の値のまま。
    applyFetched(
      fieldsRef.current.reduce((acc, field) => {
        acc[field.name] = field.value || null;
        return acc;
      }, {} as NewDataField)
    );
    fetchLatest();
    // 開いたとき・対象の記録（latestUrl）が変わったときだけ走る（fetchLatest は latestUrl でだけ変わる）。
  }, [isOpen, latestUrl, applyFetched, fetchLatest, beginRequest]);

  if (!isOpen) return null;

  // EV-334。取り直す相手があるときは、その記録の最新の値を読み終えるまで保存しない（読み込み中・失敗の後）。
  // 取り直す相手が無い使い方は従来どおり。
  const waitingLatest = latestUrl !== null && loadedUrl !== latestUrl;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    touched.current.add(name);
    setNewData((current) => ({ ...current, [name]: value }));
  };

  // ADM-04。開始・終了の組。勤怠（check_in/check_out）と作業記録（start/end）の両方を確かめる。
  // 以前は勤怠の項目名だけが対象で、作業記録は終了が開始より前でもそのまま送っていた。
  const DATE_TIME_PAIRS = [
    { startDate: 'check_in_date', startTime: 'check_in_time', endDate: 'check_out_date', endTime: 'check_out_time' },
    { startDate: 'startDate', startTime: 'startTime', endDate: 'endDate', endTime: 'endTime' },
  ];

  const value = (name: string) => (newData[name] ? String(newData[name]) : '');
  // 開いたときの値（欄の value）と元の時刻（originalIso）。照合の懸念4（2026-10-11）。
  const fieldOf = (name: string) => fields.find((field) => field.name === name);
  const initial = (name: string) => String(fieldOf(name)?.value ?? '');
  const original = (timeName: string) => fieldOf(timeName)?.originalIso ?? null;
  /** 日付・時刻の組を、開いたときから変えたか。 */
  const pairChanged = (dateName: string, timeName: string) =>
    value(dateName) !== initial(dateName) ||
    value(timeName).slice(0, 5) !== initial(timeName).slice(0, 5);

  /**
   * EV-116 段階2（2026-10-11 PM 決定7）。時刻の欄ごとの夏時間の状態（業務時間帯で読む）。
   * 存在しない時刻は断り（保存しない）、2回ある時刻は先の方として保存する（案内だけ）。
   */
  const dstStatusByField: { [key: string]: 'ok' | 'nonexistent' | 'ambiguous' } = {};
  for (const pair of DATE_TIME_PAIRS) {
    if (!fields.some((field) => field.name === pair.startDate)) continue;
    for (const [dateName, timeName] of [
      [pair.startDate, pair.startTime],
      [pair.endDate, pair.endTime],
    ]) {
      const status = businessLocalTimeStatus(value(dateName), value(timeName).slice(0, 5));
      // 2回ある時刻の案内は、利用者が値を変えて2回ある時刻になったときだけ出す。変えていなければ、
      // 保存は元の時刻のまま（businessDateTimeToIsoKeeping）なので「先の方として保存」は当たらない。
      dstStatusByField[timeName] =
        status === 'ambiguous' && !pairChanged(dateName, timeName) ? 'ok' : status;
    }
  }
  const dstMessages = {
    nonexistent: t('time-zone.dst-nonexistent', { ns: 'common' }),
    ambiguous: t('time-zone.dst-ambiguous', { ns: 'common' }),
  };

  const validateDates = () => {
    const validationErrors: { [key: string]: string | null } = {};
    // EV-116 段階2。前後は業務時間帯の時刻（UTC）で比べる。以前は端末の時間帯の Date で比べていた。
    // 照合の懸念4（2026-10-11）。実際に保存する値（元の値を変えていなければ元の時刻）で比べる。
    // fold=0 で比べると、出勤 01:50 PDT・退勤 01:10 PST の正しい記録を「終了が開始より前」で断っていた。
    const toDateTime = (date: string, time: string, timeName: string) =>
      date && time
        ? Date.parse(businessDateTimeToIsoKeeping(date, time.slice(0, 5), original(timeName)) ?? '')
        : null;

    for (const pair of DATE_TIME_PAIRS) {
      if (!fields.some((field) => field.name === pair.startDate)) continue;

      // 日付だけ・時刻だけでは時刻を組み立てられない（不正な時刻を送らない）。
      for (const [dateName, timeName] of [
        [pair.startDate, pair.startTime],
        [pair.endDate, pair.endTime],
      ]) {
        if (Boolean(value(dateName)) !== Boolean(value(timeName))) {
          validationErrors[value(dateName) ? timeName : dateName] = t('edit.error_incomplete', {
            ns: 'attendance',
          });
        }
      }

      // 存在しない時刻は保存しない（サーバーへ送らない）。
      for (const timeName of [pair.startTime, pair.endTime]) {
        if (dstStatusByField[timeName] === 'nonexistent') {
          validationErrors[timeName] = dstMessages.nonexistent;
        }
      }

      const start = toDateTime(value(pair.startDate), value(pair.startTime), pair.startTime);
      const end = toDateTime(value(pair.endDate), value(pair.endTime), pair.endTime);
      if (start !== null && end !== null && end < start) {
        validationErrors[pair.endTime] = t('edit.error_time', { ns: 'attendance' });
        if (value(pair.endDate) < value(pair.startDate)) {
          validationErrors[pair.endDate] = t('edit.error_date', { ns: 'attendance' });
        }
      }
    }

    // BE-09-AC5（RA-AC2、2026-10-11 PM 承認の D3・D4）。勤怠・作業の修正は理由が必須（サーバーも 400）。
    // 前後の空白を除いて空なら送らない。サーバーの上限（500文字）を超えても送らない。
    const reasonField = fields.find((field) => field.type === REASON_FIELD_TYPE);
    if (reasonField) {
      const reason = value(reasonField.name).trim();
      if (!reason) {
        validationErrors[reasonField.name] = t('edit.reason-required', { ns: 'attendance' });
      } else if (Array.from(reason).length > REASON_MAX_LENGTH) {
        validationErrors[reasonField.name] = t('edit.reason-too-long', { ns: 'attendance' });
      }
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = async (e: FormEvent<Element>) => {
    e.preventDefault();
    // EV-322（FR-AC5）・EV-334。最新の値を読み終えるまで（最初の読み込み・失敗の後・再読み込みの間）保存しない。
    if (waitingLatest) return;

    if (!validateDates()) {
      return;
    }

    const editFormData: any = { ...newData };

    // ADM-15。送信のあとに初期値へ戻すと、失敗して開いたままのときに入力が消えていた。
    // 開き直したときに fields から作り直す。
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      await handleSubmit(e, editFormData);
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {title}
                  </h3>
                  <button type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleFormSubmit}>
                  {fetchError ? (
                    // 文がダイアログの端に付かないよう、欄と同じ横の余白に収める。
                    <div className="mx-4 sm:mx-6 text-center">
                      <FetchFailed
                        error={fetchError}
                        onRetry={fetchLatest}
                        message={t('ui.detail-fetch-failed', { ns: 'common' })}
                      />
                    </div>
                  ) : waitingLatest ? (
                    <p role="status" className="pt-4 text-center text-gray-500">
                      {t('ui.loading', { ns: 'common' })}
                    </p>
                  ) : null}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6">
                    {fields.map((field, index) => (
                      <div key={index} className={field.type === REASON_FIELD_TYPE ? 'sm:col-span-2' : undefined}>
                        <label
                          className="block mb-1 text-sm font-regular text-neutral/100"
                          htmlFor={`${fieldId}-${index}`}
                        >
                          {field.label}{' '}
                          <span className="text-error">
                            {field.isRequired === true && '*'}
                          </span>
                        </label>
                        {field.type === 'select' ? (
                          <div className="relative">
                            <select
                              name={field.name}
                              id={`${fieldId}-${index}`}
                              className={`w-full mt-1 p-2 border ${
                                errors[field.name]
                                  ? 'border-red-500'
                                  : 'border-neutral/50'
                              } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none`}
                              required={field.isRequired}
                              // EV-333（DS-AC1）。注記（下の FetchFailed）を出す条件とそろえる。onRetry の無い欄で在りもしない id を指さない。
                              aria-describedby={field.fetchError && field.onRetry ? `${fieldId}-${index}-note` : undefined}
                              value={newData[field.name] || ''}
                              onChange={handleChange}
                            >
                              {field.options?.map((option) => (
                                <option value={option.value} id={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral/30 pr-4 ">
                              <img src={chevron_down} alt="" />
                            </div>
                          </div>
                        ) : field.type === REASON_FIELD_TYPE ? (
                          // BE-09-AC5（RA-AC1、D1・D2）。修正の理由は複数行の欄で入れる。
                          <textarea
                            name={field.name}
                            id={`${fieldId}-${index}`}
                            className={`w-full mt-1 p-2 border ${
                              errors[field.name]
                                ? 'border-red-500'
                                : 'border-neutral/50'
                            } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md px-4 py-2`}
                            rows={3}
                            aria-required="true"
                            placeholder={t('edit.reason-placeholder', { ns: 'attendance' })}
                            value={newData[field.name] || ''}
                            // handleChange は name・value だけを読む（admin-a の変更と重ならないよう型をここで合わせる）。
                            onChange={(e) =>
                              handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>)
                            }
                          />
                        ) : (
                          <input
                            type={field.type}
                            name={field.name}
                            id={`${fieldId}-${index}`}
                            className={`w-full mt-1 p-2 border ${
                              errors[field.name]
                                ? 'border-red-500'
                                : 'border-neutral/50'
                            } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none`}
                            required={field.isRequired}
                            placeholder={
                              field.type === 'date' ? 'yyyy/mm/dd' : ''
                            }
                            value={newData[field.name] || ''}
                            onChange={handleChange}
                          />
                        )}
                        {errors[field.name] ? (
                          <span className="text-error">
                            {errors[field.name]}
                          </span>
                        ) : dstStatusByField[field.name] === 'nonexistent' ? (
                          <span className="text-error">{dstMessages.nonexistent}</span>
                        ) : dstStatusByField[field.name] === 'ambiguous' ? (
                          <span className="text-caption text-neutral/100">{dstMessages.ambiguous}</span>
                        ) : field.type === REASON_FIELD_TYPE ? (
                          // D5。修正の前後と理由が記録に残ることを示す。
                          <span className="text-caption text-neutral/100">
                            {t('edit.reason-note', { ns: 'attendance' })}
                          </span>
                        ) : null}
                        {field.fetchError && field.onRetry ? (
                          <FetchFailed
                            id={`${fieldId}-${index}-note`}
                            compact
                            error={field.fetchError}
                            onRetry={field.onRetry}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || waitingLatest}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                    >
                      {confirmLabel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDialog;
